import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import { UserDisplayName } from "@/components/display/UserDisplayName";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useGrantsData } from "@/data/use-grants-data";
import { useGroupData } from "@/data/use-groups-data";
import { useGroupTemplatesData } from "@/data/use-templates-data";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

// ============================================================
// Template Row
// ============================================================

const TemplateRow = ({
  template,
  groupUuid,
}: {
  template: {
    uuid: string;
    name: string;
    description: string;
    user: { name: string } | null;
    group: { name: string } | null;
  };
  groupUuid: string;
}) => {
  const { grants, upsert, revoke } = useGrantsData({
    templateUuid: template.uuid,
  });
  const [isPending, setIsPending] = useState(false);
  const isEnabled = grants.some((grant) => grant.group.uuid === groupUuid);

  const handleToggle = async (checked: boolean) => {
    setIsPending(true);
    try {
      if (checked) {
        await upsert({ groupUuid, access: "write" });
      } else {
        await revoke({ groupUuid });
      }
    } catch {
      toast.error("Failed to update template access.");
    } finally {
      setIsPending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{template.name}</TableCell>
      <TableCell className="text-muted-foreground">
        {template.description}
      </TableCell>
      <TableCell>
        {template.user ? <UserDisplayName user={template.user} /> : "—"}
      </TableCell>
      <TableCell>
        <GroupOrUnknownDisplayName group={template.group} />
      </TableCell>
      <TableCell>
        <Switch
          checked={isEnabled}
          disabled={isPending}
          onCheckedChange={handleToggle}
        />
      </TableCell>
    </TableRow>
  );
};

// ============================================================
// Page
// ============================================================

export const GlobalAdminGroupTemplatesPage = () => {
  const { groupUuid } = useParams({ strict: false }) as {
    groupUuid?: string;
  };
  const { group } = useGroupData(groupUuid);
  const parentGroupUuid = group?.parentGroupUuid ?? null;
  const { group: parentGroup } = useGroupData(parentGroupUuid);
  const { templates: parentTemplates, isLoading } = useGroupTemplatesData({
    groupUuid: parentGroupUuid,
  });

  if (!group) return null;

  return (
    <Page>
      <PageHeader>
        <PageHeader.Title>Templates</PageHeader.Title>
      </PageHeader>
      <PageBody>
        {!parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            This group has no parent group. There are no parent artifacts to
            manage.
          </p>
        )}
        {parentGroupUuid && (
          <p className="text-muted-foreground text-sm">
            These templates are originally shared or published in the parent
            group{" "}
            <span className="text-foreground font-medium">
              {parentGroup?.name ?? parentGroupUuid}
            </span>
            .
          </p>
        )}
        {parentGroupUuid && isLoading && <TableSkeleton />}
        {parentGroupUuid && !isLoading && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Access</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {parentTemplates.map((template) => (
                <TemplateRow
                  key={template.uuid}
                  template={template}
                  groupUuid={groupUuid!}
                />
              ))}
              {parentTemplates.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground py-4 text-center"
                  >
                    No templates in the parent group.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </PageBody>
    </Page>
  );
};
